import { Box, Container, Typography, Link } from '@mui/material'

export default function PrivacyPolicyPage() {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: 6 }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box component="img" src="/omnitrace_logo.png" alt="OmniTrace" sx={{ width: 36, height: 36 }} />
          <Typography variant="h5" fontWeight={800} color="#0f172a">OmniTrace</Typography>
        </Box>

        <Typography variant="h4" fontWeight={800} color="#0f172a" gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="#64748b" sx={{ mb: 4 }}>
          Last updated: April 17, 2026
        </Typography>

        <Section title="1. Introduction">
          OmniTrace ("we", "our", "us") operates the OmniTrace GPS fleet tracking platform,
          including the website at omnitrace.app, the mobile applications for iOS and Android,
          and related services (collectively, the "Service"). This Privacy Policy explains how
          we collect, use, disclose, and safeguard your information when you use our Service.
        </Section>

        <Section title="2. Information We Collect">
          <b>Account Information:</b> Name, email address, phone number, organization name,
          and role when you register or are added by an administrator.
          <br /><br />
          <b>Device & Vehicle Data:</b> GPS location, speed, heading, altitude, ignition state,
          sensor readings (fuel level, temperature, battery voltage), and device identifiers (IMEI/serial).
          <br /><br />
          <b>Usage Data:</b> App interactions, pages viewed, features used, session duration,
          crash reports, and performance metrics collected via Sentry and Mixpanel.
          <br /><br />
          <b>Authentication Data:</b> Encrypted passwords, two-factor authentication tokens,
          biometric enrollment status, and trusted device identifiers.
        </Section>

        <Section title="3. How We Use Your Information">
          We use collected information to:
          <br />• Provide real-time GPS tracking and fleet management services
          <br />• Generate trip reports, analytics, and driver safety scores
          <br />• Send push notifications, alerts, and email reports
          <br />• Authenticate users and manage access control
          <br />• Improve our Service through usage analytics
          <br />• Comply with legal obligations
        </Section>

        <Section title="4. Data Sharing">
          We do not sell your personal information. We may share data with:
          <br />• <b>Your organization:</b> Administrators in your tenant can view device data,
          trips, and user activity within their organization.
          <br />• <b>Trust Circle partners:</b> If your administrator grants cross-tenant device
          access, shared device data is visible to the partner organization.
          <br />• <b>Service providers:</b> We use third-party services including Sentry (error tracking),
          Mixpanel (analytics), OneSignal (push notifications), and cloud hosting providers.
          <br />• <b>Legal requirements:</b> When required by law, regulation, or legal process.
        </Section>

        <Section title="5. Data Security">
          We implement industry-standard security measures including:
          <br />• TLS encryption for all data in transit
          <br />• Encrypted password storage (bcrypt)
          <br />• JWT-based authentication with short-lived tokens
          <br />• Role-based access control
          <br />• Regular security audits
        </Section>

        <Section title="6. Data Retention">
          GPS and sensor data is retained for 90 days in our time-series database.
          Trip records, alerts, and user data are retained for the duration of your
          subscription. Upon account deletion, all associated data is permanently removed
          within 30 days.
        </Section>

        <Section title="7. Your Rights">
          You have the right to:
          <br />• Access your personal data
          <br />• Request correction of inaccurate data
          <br />• Request deletion of your account and data
          <br />• Opt out of analytics tracking (via app settings)
          <br />• Revoke trusted devices and active sessions
          <br /><br />
          Contact us at <Link href="mailto:privacy@omnitrace.app">privacy@omnitrace.app</Link> to exercise these rights.
        </Section>

        <Section title="8. Children's Privacy">
          Our Service is not directed to individuals under 16. We do not knowingly
          collect personal information from children.
        </Section>

        <Section title="9. Changes to This Policy">
          We may update this Privacy Policy from time to time. We will notify you of
          material changes via email or in-app notification.
        </Section>

        <Section title="10. Contact Us">
          If you have questions about this Privacy Policy, contact us at:
          <br />Email: <Link href="mailto:privacy@omnitrace.app">privacy@omnitrace.app</Link>
          <br />Website: <Link href="https://omnitrace.app">omnitrace.app</Link>
        </Section>

        <Typography variant="body2" color="#94a3b8" sx={{ mt: 6, textAlign: 'center' }}>
          © {new Date().getFullYear()} OmniTrace. All rights reserved.
        </Typography>
      </Container>
    </Box>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" fontWeight={700} color="#1e293b" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body1" color="#475569" sx={{ lineHeight: 1.8 }}>
        {children}
      </Typography>
    </Box>
  )
}
